/**
 * Shared A2A agent infrastructure — single source of truth for
 * authenticated fetch, MAF card resolution, and SDK factory config.
 *
 * Feature-specific agent hooks (CustomerConfidenceCall, RecommendationInput)
 * import from here instead of duplicating the boilerplate.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { A2A_PROTOCOL_VERSION } from "@a2a-js/sdk";
import { JsonRpcTransportFactory, RestTransportFactory, } from "@a2a-js/sdk/client";
import { getAccessToken } from "@elixir/auth";
/** Base URL used by all agent endpoints (env-injected by webpack). */
export const agentBaseUrl = process.env.FRONTDOOR_BASEURL || "https://localhost:7214";
/**
 * Fetch wrapper that attaches an AAD Bearer token to every request.
 * Required by the A2A SDK because it bypasses HttpService and uses
 * raw `fetch` for streaming support.
 */
export const authenticatedFetch = (input, init) => __awaiter(void 0, void 0, void 0, function* () {
    const token = yield getAccessToken();
    const headers = new Headers(init === null || init === void 0 ? void 0 : init.headers);
    if (token) {
        headers.set("Authorization", `Bearer ${token}`);
    }
    else {
        // eslint-disable-next-line no-console
        console.warn("[agentConfig] authenticatedFetch: getAccessToken returned null — request will be unauthenticated.");
    }
    return fetch(input, Object.assign(Object.assign({}, init), { headers }));
});
/**
 * Custom card resolver for MAF (Microsoft Agent Framework) agent cards.
 * Fetches the card from /v1/card and normalises the URL by stripping
 * the suffix so the SDK can derive the base agent URL.
 *
 * This is necessary because the .well-known endpoint only supports a
 * single agent per the A2A spec.
 */
export class MAFCardResolver {
    resolve(baseUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield authenticatedFetch(`${baseUrl}/v1/card`);
            if (!response.ok) {
                throw new Error(`Failed to fetch card from ${baseUrl}/v1/card: ${response.statusText}`);
            }
            const cardData = (yield response.json());
            // ClientFactory.createFromUrl does not invoke the normalizeAgentCard
            // hook, so the resolver has to apply it itself.
            return this.normalizeAgentCard(cardData, baseUrl);
        });
    }
    /**
     * Synthesises the transport interface the backend omits.
     *
     * The Escalation Manager backend returns `supportedInterfaces: []`, which
     * leaves ClientFactory with no transport to select and makes it throw
     * "No compatible transport found".  A2A v1.0 replaced the v0.3 top-level
     * `url` / `preferredTransport` fields with this array, so we rebuild the
     * single entry the backend actually serves: JSON-RPC over the proxy URL.
     *
     * The proxy URL is used deliberately - the backend's self-referential URL
     * points at the Container App's private address, which is inaccessible
     * from the browser.  The proxy is the only valid entry point for all
     * subsequent A2A operations, so it is pinned unconditionally, matching the
     * guarantee the v0.3 resolver gave via `cardData.url = baseUrl`.
     */
    normalizeAgentCard(card, baseUrl) {
        if (typeof card !== "object" || card === null) {
            throw new Error("Invalid agent card: expected a JSON object.");
        }
        const agentCard = card;
        // The transport endpoint is pinned to the proxy URL regardless of what the
        // card advertises. A card-supplied URL must never be used: the SDK passes
        // it verbatim to the transport, and both transports carry an authenticating
        // fetch, so an attacker-controlled URL would leak the bearer token
        // cross-origin. The interface is constructed only from the pinned fields
        // the transport needs; all card-advertised interface metadata is discarded.
        const jsonRpcInterface = {
            url: baseUrl !== null && baseUrl !== void 0 ? baseUrl : agentBaseUrl,
            protocolBinding: "JSONRPC",
            protocolVersion: A2A_PROTOCOL_VERSION,
            tenant: "",
        };
        return Object.assign(Object.assign({}, agentCard), { supportedInterfaces: [jsonRpcInterface] });
    }
}
/** Pre-configured SDK factory options with authenticated transports. */
export const agentFactoryConfig = {
    transports: [
        new JsonRpcTransportFactory({ fetchImpl: authenticatedFetch }),
        new RestTransportFactory({ fetchImpl: authenticatedFetch }),
    ],
    cardResolver: new MAFCardResolver(),
};
